import type { EditorState, LexicalEditor } from 'lexical';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLayoutEffect, useRef, useCallback, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { EXTERNAL_STATE_SYNCHRONIZER_TAG } from '../ExternalStateSynchronizerPlugin/ExternalStateSynchronizerPlugin';

const DEFAULT_DEBOUNCE_TIMEOUT_MS = 500;
const MAX_WAIT_MS = 2000;

export function DebouncedOnChangePlugin({
  ignoreHistoryMergeTagChange = true,
  ignoreSelectionChange = false,
  debounceTimeoutMs = DEFAULT_DEBOUNCE_TIMEOUT_MS,
  onChange,
}: {
  ignoreHistoryMergeTagChange?: boolean;
  ignoreSelectionChange?: boolean;
  debounceTimeoutMs?: number;
  onChange: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void;
}): null {
  const [editor] = useLexicalComposerContext();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const firstChangeTimeRef = useRef<number | null>(null);
  const hasPendingChangeRef = useRef<boolean>(false);

  const debouncedOnChange = useCallback(
    (editorState: EditorState, editorInstance: LexicalEditor, tags: Set<string>) => {
      const now = Date.now();
      hasPendingChangeRef.current = true;

      // Clear existing debounce timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set first change time if not already set
      if (firstChangeTimeRef.current === null) {
        firstChangeTimeRef.current = now;

        // Set max timeout to ensure onChange is called within MAX_WAIT_MS
        maxTimeoutRef.current = setTimeout(() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          firstChangeTimeRef.current = null;
          onChange(editorState, editorInstance, tags);
        }, MAX_WAIT_MS);
      }

      // Set regular debounce timeout
      timeoutRef.current = setTimeout(() => {
        // Clear max timeout since we're calling onChange now
        if (maxTimeoutRef.current) {
          clearTimeout(maxTimeoutRef.current);
          maxTimeoutRef.current = null;
        }
        firstChangeTimeRef.current = null;
        hasPendingChangeRef.current = false;
        onChange(editorState, editorInstance, tags);
      }, debounceTimeoutMs);
    },
    [onChange, debounceTimeoutMs],
  );

  useLayoutEffect(() => {
    // Cleanup function to clear timeouts when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
        maxTimeoutRef.current = null;
      }
      firstChangeTimeRef.current = null;
    };
  }, [debouncedOnChange]);

  useLayoutEffect(() => {
    return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves, prevEditorState, tags }) => {
      if (
        (ignoreSelectionChange && dirtyElements.size === 0 && dirtyLeaves.size === 0) ||
        (ignoreHistoryMergeTagChange && tags.has('history-merge')) ||
        prevEditorState.isEmpty() ||
        // Do not call onChange if the update is coming from the external
        // state synchronizer reflecting a change of the source value
        tags.has(EXTERNAL_STATE_SYNCHRONIZER_TAG)
      ) {
        return;
      }

      if (!isEqual(prevEditorState.toJSON(), editorState.toJSON())) {
        debouncedOnChange(editorState, editor, tags);
      }
    });
  }, [editor, ignoreHistoryMergeTagChange, ignoreSelectionChange, debouncedOnChange]);

  // Call onChange on unmount to ensure the updated value is saved
  useEffect(() => {
    return () => {
      if (hasPendingChangeRef.current) {
        onChange(editor.getEditorState(), editor, new Set());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, hasPendingChangeRef]);

  return null;
}
